import React, { useRef, useState, useEffect } from 'react';
import { positioningText, stats } from '../../data/homeData';
import useScrollReveal from '../../hooks/useScrollReveal';
import useCountUp from '../../hooks/useCountUp';
import Murmuration from './Murmuration';
import './AboutSection.css';

// Toggle: 'orbs' or 'murmuration'
const BG_ANIMATION = 'murmuration';

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*';
const FIRST_TEXT = 'BIRDS OF A FEATHER, FLOCK\u00A0TOGETHER...';
const FINAL_TEXT = 'Technology Executive. AI\u00A0Builder. Commerce Leader.';

const ScrambleHeadline = () => {
  const [display, setDisplay] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [phase, setPhase] = useState('idle');
  const headlineRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = headlineRef.current;
    if (!el) return;

    const timers = [];
    const intervals = [];

    const addTimeout = (fn, ms) => {
      const id = setTimeout(fn, ms);
      timers.push(id);
      return id;
    };

    const runScramble = (text, onDone) => {
      let currentLen = 0;
      const settled = new Array(text.length).fill(false);
      const iters = text.split('').map(c => c === ' ' ? 0 : 3 + Math.floor(Math.random() * 4));

      const id = setInterval(() => {
        if (currentLen < text.length) currentLen++;

        let result = '';
        let allSettled = true;
        for (let i = 0; i < currentLen; i++) {
          if (settled[i]) {
            result += text[i];
          } else if (text[i] === ' ') {
            settled[i] = true;
            result += ' ';
          } else {
            iters[i]--;
            if (iters[i] <= 0) {
              settled[i] = true;
              result += text[i];
            } else {
              allSettled = false;
              result += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
            }
          }
        }
        for (let i = 0; i < currentLen; i++) {
          if (!settled[i]) allSettled = false;
        }
        setDisplay(result);
        if (currentLen >= text.length && allSettled) {
          clearInterval(id);
          onDone();
        }
      }, 35);
      intervals.push(id);
    };

    const runBackspace = (text, onDone) => {
      let len = text.length;
      const id = setInterval(() => {
        len -= 2;
        if (len <= 0) {
          setDisplay('');
          clearInterval(id);
          onDone();
        } else {
          setDisplay(text.slice(0, len));
        }
      }, 20);
      intervals.push(id);
    };

    const startAnimation = () => {
      if (startedRef.current) return;
      startedRef.current = true;

      addTimeout(() => {
        setPhase('typing1');
        runScramble(FIRST_TEXT, () => {
          setPhase('pause1');
          addTimeout(() => {
            setPhase('deleting');
            runBackspace(FIRST_TEXT, () => {
              addTimeout(() => {
                setPhase('typing2');
                runScramble(FINAL_TEXT, () => {
                  setPhase('done');
                  addTimeout(() => setShowCursor(false), 1500);
                });
              }, 300);
            });
          }, 5000);
        });
      }, 800);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          addTimeout(startAnimation, 1300);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    // Check if already in view
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      addTimeout(startAnimation, 1300);
    } else {
      observer.observe(el);
    }

    return () => {
      observer.disconnect();
      timers.forEach(clearTimeout);
      intervals.forEach(clearInterval);
    };
  }, []);

  const cursor = showCursor ? <span className="headline-cursor" aria-hidden="true">|</span> : null;

  const renderText = () => {
    if (phase === 'done') {
      const parts = FINAL_TEXT.split('. ');
      return parts.map((part, i) => (
        <span key={i}>
          <span className="headline-highlight">
            {part}{i < parts.length - 1 ? '.' : ''}{i === parts.length - 1 ? cursor : ''}
          </span>
          {i < parts.length - 1 ? ' ' : ''}
        </span>
      ));
    }
    if (!display) return <span className="headline-highlight">{cursor}</span>;
    return <span className="headline-highlight">{display}{cursor}</span>;
  };

  return (
    <h2 className="positioning-headline" ref={headlineRef}>
      {renderText()}
    </h2>
  );
};

const StatNumber = ({ value }) => {
  const suffix = value.replace(/[0-9]/g, '');
  const { ref, count } = useCountUp(value);

  return (
    <div className="about-stat-number" ref={ref}>
      {typeof count === 'number' ? `${count}${suffix}` : count}
    </div>
  );
};

const AboutSection = () => {
  const revealRef = useScrollReveal();
  const sectionRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { borderBoxSize } = entry;
      if (borderBoxSize && borderBoxSize[0]) {
        setDimensions({
          width: borderBoxSize[0].inlineSize,
          height: borderBoxSize[0].blockSize,
        });
      } else {
        // Fallback for older browsers
        setDimensions({
          width: entry.target.offsetWidth,
          height: entry.target.offsetHeight,
        });
      }
    });
    observer.observe(sectionRef.current, { box: 'border-box' });
    return () => observer.disconnect();
  }, []);

  const setRefs = (el) => {
    sectionRef.current = el;
    if (typeof revealRef === 'function') revealRef(el);
    else if (revealRef) revealRef.current = el;
  };

  return (
    <section id="about" className="section-card about-section reveal-section" ref={setRefs}>
      <div className="about-bg-text" aria-hidden="true">WELCOME</div>

      {BG_ANIMATION === 'orbs' && (
        <div className="about-orb" aria-hidden="true">
          <div className="orb-blur-field">
            <div className="orb-blob orb-blob-1"></div>
            <div className="orb-blob orb-blob-2"></div>
            <div className="orb-blob orb-blob-3"></div>
            <div className="orb-blob orb-blob-4"></div>
          </div>
          <div className="orb-glow"></div>
        </div>
      )}

      {BG_ANIMATION === 'murmuration' && dimensions.width > 0 && (
        <Murmuration width={dimensions.width} height={dimensions.height} />
      )}

      <div className="about-foreground">
        <ScrambleHeadline />
        <div className="positioning-body">
          <p>{positioningText}</p>
        </div>
      </div>
    </section>
  );
};

const StatsSection = () => {
  const revealRef = useScrollReveal();

  return (
    <div className="section-card stats-section reveal-section" ref={revealRef}>
      <div className="about-stats">
        {stats.map((stat, index) => (
          <div key={index} className="about-stat stagger-child">
            <StatNumber value={stat.number} />
            <div className="about-stat-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export { StatsSection };
export default AboutSection;
